import React from 'react';
import { Box, Card, CardContent, Typography, Skeleton } from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import RemoveIcon from '@mui/icons-material/Remove';

interface OverviewData {
  total_sessions: number;
  unique_visitors: number;
  total_page_views: number;
  bounce_rate: number;
  avg_session_duration: number;
  changes: {
    sessions: number;
    visitors: number;
    page_views: number;
  };
}

interface OverviewCardsProps {
  data: OverviewData | null;
  loading: boolean;
}

const formatDuration = (seconds: number): string => {
  if (seconds < 60) return `${seconds}s`;
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}m ${secs}s`;
};

const formatNumber = (num: number): string => {
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num.toString();
};

interface StatCardProps {
  title: string;
  value: string | number;
  change?: number;
  loading: boolean;
}

const StatCard: React.FC<StatCardProps> = ({ title, value, change, loading }) => {
  const getChangeIcon = () => {
    if (change === undefined || change === 0) {
      return <RemoveIcon fontSize="small" sx={{ color: 'text.secondary' }} />;
    }
    return change > 0 ? (
      <TrendingUpIcon fontSize="small" sx={{ color: 'success.main' }} />
    ) : (
      <TrendingDownIcon fontSize="small" sx={{ color: 'error.main' }} />
    );
  };

  const getChangeColor = () => {
    if (change === undefined || change === 0) return 'text.secondary';
    return change > 0 ? 'success.main' : 'error.main';
  };

  return (
    <Card sx={{ flex: 1, minWidth: 140 }}>
      <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
          {title}
        </Typography>
        {loading ? (
          <Skeleton width={60} height={32} />
        ) : (
          <Typography variant="h5" sx={{ fontWeight: 600, my: 0.5 }}>
            {value}
          </Typography>
        )}
        {change !== undefined && !loading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {getChangeIcon()}
            <Typography variant="caption" sx={{ color: getChangeColor() }}>
              {change > 0 ? '+' : ''}{change}%
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

const OverviewCards: React.FC<OverviewCardsProps> = ({ data, loading }) => {
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
      <StatCard
        title="Visitors"
        value={data ? formatNumber(data.unique_visitors) : '-'}
        change={data?.changes?.visitors}
        loading={loading}
      />
      <StatCard
        title="Sessions"
        value={data ? formatNumber(data.total_sessions) : '-'}
        change={data?.changes?.sessions}
        loading={loading}
      />
      <StatCard
        title="Page Views"
        value={data ? formatNumber(data.total_page_views) : '-'}
        change={data?.changes?.page_views}
        loading={loading}
      />
      <StatCard
        title="Bounce Rate"
        value={data ? `${data.bounce_rate}%` : '-'}
        loading={loading}
      />
      <StatCard
        title="Avg. Duration"
        value={data ? formatDuration(data.avg_session_duration) : '-'}
        loading={loading}
      />
    </Box>
  );
};

export default OverviewCards;
